import { z } from 'zod';

// OBS-02 — fixed, capped client-error schema (docs/architecture/16-observability-and-operational-safety.md §5).
// Kept DB-free so it can be unit-tested without the Supabase client. The sink route
// imports this; `.strict()` rejects any field outside this set (doc 16 §8 — "never
// accepts arbitrary payloads").

export const MAX_ROUTE_LENGTH = 200;
export const MAX_MESSAGE_LENGTH = 1000;
export const MAX_STACK_LENGTH = 4000;
export const MAX_CORRELATION_ID_LENGTH = 64;
export const MAX_ORIGIN_LENGTH = 120;
/**
 * Build/release identifier (OBS-02b). Lets the sink pick the matching source map
 * to symbolicate a minified stack later. Optional: an older client that does not
 * send one still reports, just without symbolication.
 */
export const MAX_RELEASE_LENGTH = 80;

/**
 * Pre-parse body guard. DERIVED from the field caps above — never a literal.
 *
 * It used to be a flat 4096, and the two halves of the contract disagreed:
 * `buildClientErrorPayload` truncates to the field caps, which sum to ~5,384
 * characters before JSON syntax, so the client was built to send ~5.5 KB while
 * the sink refused anything over 4 KB. Any error carrying a stack longer than
 * roughly 2,800 characters was answered 413 and thrown away — and the schema
 * below still advertised `stack.max(4000)`, a value that could not physically
 * arrive, because 4,000 characters of stack plus the mandatory route and message
 * already exceeded the cap.
 *
 * The reports lost were exactly the ones worth having: the biggest stacks are the
 * worst crashes. Two real 413s were observed in minutes of ordinary local use,
 * and because this IS the error channel, its own failure is invisible — a
 * dropped report and no error look identical from the application side.
 *
 * The doubling covers JSON escaping (a newline-heavy stack becomes `\n` pairs)
 * and multi-byte UTF-8, since `content-length` counts BYTES while the caps count
 * characters. The addend covers field names, braces, quotes and commas. The cap
 * still does its real job — rejecting arbitrary payloads before parsing — it
 * just no longer rejects payloads this service itself produces.
 */
export const MAX_BODY_BYTES =
  (MAX_ROUTE_LENGTH +
    MAX_MESSAGE_LENGTH +
    MAX_STACK_LENGTH +
    MAX_CORRELATION_ID_LENGTH +
    MAX_ORIGIN_LENGTH +
    MAX_RELEASE_LENGTH) *
    2 +
  512;

export const CLIENT_ERROR_ID = /^[A-Za-z0-9_-]{1,64}$/;

export const clientErrorSchema = z
  .object({
    route: z.string().min(1).max(MAX_ROUTE_LENGTH),
    correlation_id: z
      .string()
      .max(MAX_CORRELATION_ID_LENGTH)
      .regex(CLIENT_ERROR_ID)
      .optional(),
    message: z.string().min(1).max(MAX_MESSAGE_LENGTH),
    stack: z.string().max(MAX_STACK_LENGTH).optional(),
    level: z.enum(['info', 'warn', 'error']).optional(),
    // Which host the report came from (audit ISSUE-26). Without it a crash on
    // www.menuraq.com is indistinguishable from one on a developer's
    // localhost:3001 — and because local and production share one database,
    // that made the whole channel unattributable. An audit of 173 reports could
    // not tell customer impact from development noise.
    origin: z.string().max(MAX_ORIGIN_LENGTH).optional(),
    // Build id to symbolicate a minified stack against (OBS-02b).
    release: z.string().max(MAX_RELEASE_LENGTH).optional(),
  })
  .strict();
